import { useState, useEffect } from "react";
import { Routes, Route, useLocation, Navigate } from "react-router-dom";
import "./App.css";
import AddProduct from "./components/AddProduct";
import HomePage from "./components/HomePage";
import Navbar from "./components/Navbar";
import OrderHistory from "./components/OrderHistory";
import Login from "./components/Login&Register/Login";
import Register from "./components/Login&Register/Register";
import Expensises from "./components/Expensises";
import Categories from "./components/Categories";
import AdminPanel from "./components/Admin/AdminPanel";
import Products from "./components/Products";
import Profile from "./components/Profile";
import CustomTable from "./components/CustomTable";
import Structure from "./components/Structure";
import RequireAuth from "./components/authentication/PrivateRoute";
import BillView from "./components/BillView";
import ForgotPassword from "./components/Login&Register/ForgotPassword";
import ResetPasswordPage from "./components/Login&Register/ResetPasswordPage";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import SuperAdminDashboard from "./SuperAdmin/SuperAdminDashboard";
import SubscriptionPlans from "./components/Subscription/SubscriptionPlans";
import { useAuth } from "./components/authentication/AuthContext";
import SubscriptionOverlay from "./components/SubscriptionOverlay";
import SubscriptionPage from "./components/Subscription/SubscriptionPage";
import RequireSuperAdmin from "./components/authentication/RequireSuperAdmin";
import SelectPlace from "./components/SelectPlace";
import AdminLayoutPage from "./components/AdminLayoutPage";
import CustomersPage from "./components/CustomerPage";
import PublicRoutes from "./customers/PublicRoutes";
import PublicQRPage from "./customers/PublicQRPage";
import { PublicOrderProvider } from "./context/PublicOrderContext";
import RequireRole from "./components/authentication/RequireRole";
import StaffManagement from "./components/StaffManagement";
import KdsDashboard from "./components/KdsDashboard";
import SyncIndicator from "./components/SyncIndicator";
import { runSync } from "./utils/syncManager";
import OngoingOrders from "./components/OngoingOrders";
import InventoryPage from "./components/InventoryPage";
import RecipeManager from "./components/RecipeManager";
import AnalyticsDashboard from "./components/Admin/AnalyticsDashboard";
import ProductAnalytics from "./components/Admin/ProductAnalytics";
import Ledger from "./components/Ledger";
import ImpersonationBanner from "./components/ImpersonationBanner";
import OffersManagement from "./Admin/OffersManagement";
import PublicOfferPage from "./customers/PublicOfferPage";
import CaptainView from "./components/CaptainView";
import PublicBookingPage from "./customers/PublicBookingPage";
import PublicFeedbackPage from "./customers/PublicFeedbackPage";

// The old Process page listed bills in a format the server no longer returns.
// Active orders now live on Ongoing Orders; chefs work from the kitchen display.
function ProcessRedirect() {
  const { isStaff, staffRole } = useAuth();
  return <Navigate to={isStaff && staffRole === "chef" ? "/kds" : "/ongoing-orders"} replace />;
}

function App() {
  const { isAuthenticated, subscription, role, loading, user, isImpersonating } = useAuth();
  const location = useLocation();
  const isBill = location.pathname.startsWith("/bill");
  const isPublicRoute = location.pathname.startsWith("/r/") || location.pathname.startsWith("/restaurant/") || location.pathname.startsWith("/book/") || location.pathname.startsWith("/feedback/");
  const [isSyncing, setIsSyncing] = useState(false);

  // ── Global "click outside to close" for every modal ──────────────────────
  // All modals in the app render a full-screen dimmed backdrop (fixed inset-0
  // bg-black/... or bg-slate-900/...). When the user presses AND releases on
  // that backdrop (not on the modal card), we trigger the modal's own
  // Close / Cancel / ✕ button. Tracking mousedown prevents accidental closes
  // when a drag (e.g. text selection) ends outside the card.
  useEffect(() => {
    const isBackdrop = (el) =>
      el instanceof HTMLElement &&
      el.classList.contains("fixed") &&
      el.classList.contains("inset-0") &&
      /bg-(black|slate-900)/.test(el.className);

    const findCloseButton = (overlay) => {
      const buttons = [...overlay.querySelectorAll("button")];
      return (
        buttons.find((b) => b.textContent.trim().toLowerCase() === "close") ||
        buttons.find((b) => ["cancel", "✕", "×"].includes(b.textContent.trim().toLowerCase())) ||
        buttons.find((b) => b.querySelector("svg.lucide-x, .fa-times"))
      );
    };

    let pressedBackdrop = null;
    const onMouseDown = (e) => {
      pressedBackdrop = isBackdrop(e.target) ? e.target : null;
    };
    const onMouseUp = (e) => {
      if (pressedBackdrop && e.target === pressedBackdrop) {
        const btn = findCloseButton(pressedBackdrop);
        if (btn) btn.click();
      }
      pressedBackdrop = null;
    };

    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("mouseup", onMouseUp);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("mouseup", onMouseUp);
    };
  }, []);

  // Fix for QR Scanners that strip the hash (#) from the URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("qr") === "true") {
      const slug = params.get("slug");
      const floorName = params.get("floorName");
      const sectionName = params.get("sectionName");
      const unitNumber = params.get("unitNumber");
      const sectionType = params.get("sectionType");
      const roomType = params.get("roomType") || "";

      // Single navigation — avoids a HashRouter render of Login between the two steps
      window.location.replace(
        `${window.location.origin}/#/r/${slug}?floorName=${encodeURIComponent(floorName)}&sectionName=${encodeURIComponent(sectionName)}&unitNumber=${unitNumber}&sectionType=${sectionType}&roomType=${encodeURIComponent(roomType)}`
      );
    }
  }, []);

  useEffect(() => {
    let syncTimer = null;
    let requestTimer = null;

    const syncOfflineOrders = async () => {
      try {
        const summary = await runSync({ onStart: () => setIsSyncing(true) });
        if (!summary) return;

        if (summary.synced > 0) {
          console.log(`[Sync Manager] Synced ${summary.synced} offline action(s).`);
          window.dispatchEvent(new CustomEvent("sync-complete", { detail: { idMap: summary.idMap } }));
        }
        if (summary.failed > 0) {
          toast.error(
            `${summary.failed} offline change(s) were rejected by the server: ${summary.failures[0]?.message || "see sync status"}`,
            { autoClose: 10000 }
          );
        }
        if (summary.stoppedBy === "blocked") {
          toast.warn("Offline orders are waiting to sync — please log in again or renew your plan.", { toastId: "sync-blocked" });
        }
      } catch (err) {
        console.error(`[Sync Manager] Synchronization error:`, err);
      } finally {
        setIsSyncing(false);
      }
    };

    // Coalesce bursts of requests (e.g. several actions queued while online)
    const handleSyncRequest = () => {
      clearTimeout(requestTimer);
      requestTimer = setTimeout(syncOfflineOrders, 500);
    };

    window.addEventListener("online", syncOfflineOrders);
    window.addEventListener("cloudrasoi-sync-request", handleSyncRequest);

    syncOfflineOrders();
    syncTimer = setInterval(syncOfflineOrders, 15000);

    return () => {
      window.removeEventListener("online", syncOfflineOrders);
      window.removeEventListener("cloudrasoi-sync-request", handleSyncRequest);
      clearInterval(syncTimer);
      clearTimeout(requestTimer);
    };
  }, []);

  useEffect(() => {
    if (isImpersonating) {
      document.body.style.paddingTop = "82px";
    } else {
      document.body.style.paddingTop = "50px";
    }
    return () => {
      document.body.style.paddingTop = "50px";
    };
  }, [isImpersonating]);


  const excludedRoutes = ["/subscription-plans"];
  const showOverlay =
    !loading &&
    isAuthenticated &&
    Number(role) !== 2 &&
    user?.registrationType !== "admin" &&
    subscription?.isActive === false &&
    !excludedRoutes.includes(location.pathname);

  return (
    <>
      <ImpersonationBanner />
      {!isBill && !isPublicRoute && <Navbar />}
      <ToastContainer style={{ zIndex: 999999 }} />
      <Routes>
        <Route path="/r/:slug/*" element={<PublicOrderProvider><PublicRoutes /></PublicOrderProvider>} />
        {/* Public Routes */}
        <Route path="/restaurant/:restaurantId/offers" element={<PublicOfferPage />} />
        <Route path="/book/:slug" element={<PublicBookingPage />} />
        <Route path="/feedback/:slug" element={<PublicFeedbackPage />} />
        <Route path="/" element={<Login />} />
        <Route path="/bill/:orderId" element={<BillView />} />
        {/* <Route path="/bill/:orderId/pay" element={<BillView />} /> */}
        <Route path="/register" element={<Register />} />
        <Route path="/ForgotPassword" element={<ForgotPassword />} />
        <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
        
        

        {/* ============= ALL PROTECTED ROUTES ============= */}

        <Route element={<RequireAuth />}>
          {/* General routes accessible to all roles */}
          <Route path="/subscription-plans" element={<SubscriptionPlans />} />
          <Route path="/subscription-card" element={<SubscriptionPage />} />

          {/* POS & Table Ordering (Waiter, Cashier, Manager, Owner) */}
          <Route element={<RequireRole allowedRoles={["waiter", "cashier", "manager"]} />}>
            <Route path="/home" element={<HomePage />} />
            <Route path="/select-place" element={<SelectPlace />} />
            <Route path="/table" element={<CustomTable />} />
            <Route path="/ongoing-orders" element={<OngoingOrders />} />
            <Route path="/captain" element={<CaptainView />} />
          </Route>

          {/* Kitchen Display / Order Process (Waiter, Chef, Cashier, Manager, Owner) */}
          <Route element={<RequireRole allowedRoles={["waiter", "chef", "cashier", "manager"]} />}>
            <Route path="/process" element={<ProcessRedirect />} />
          </Route>

          {/* KDS Dashboard (Chef, Manager, Owner) */}
          <Route element={<RequireRole allowedRoles={["chef", "manager"]} />}>
            <Route path="/kds" element={<KdsDashboard />} />
          </Route>

          {/* Billing, Order History, CRM Customers (Cashier, Manager, Owner) */}
          <Route element={<RequireRole allowedRoles={["cashier", "manager"]} />}>
            <Route path="/history" element={<OrderHistory />} />
            <Route path="/customers" element={<CustomersPage />} />
          </Route>

          {/* Expense Tracking (Cashier, Manager, Owner) */}
          <Route element={<RequireRole allowedRoles={["cashier", "manager"]} />}>
            <Route path="/expensises" element={<Expensises />} />
            <Route path="/ledger" element={<Ledger />} />
          </Route>

          {/* Menu Catalog, Layout Design, Store Admin Profile (Manager, Owner) */}
          <Route element={<RequireRole allowedRoles={["manager"]} />}>
            <Route path="/add-product" element={<AddProduct />} />
            <Route path="/categories" element={<Categories />} />
            <Route path="/products" element={<Products />} />
            <Route path="/structure" element={<AdminLayoutPage />} />
            <Route path="/admin" element={<AdminPanel />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/dashboard/qr/:slug" element={<PublicQRPage />} />
            <Route path="/inventory" element={<InventoryPage />} />
            <Route path="/recipes" element={<RecipeManager />} />
            <Route path="/analytics" element={<AnalyticsDashboard />} />
            <Route path="/product-analytics" element={<ProductAnalytics />} />
            <Route path="/offers-management" element={<OffersManagement />} />
          </Route>

          {/* Staff Account Management (Owner only) */}
          <Route element={<RequireRole allowedRoles={[]} />}>
            <Route path="/staff" element={<StaffManagement />} />
          </Route>
        </Route>

        {/* ===== SUPER ADMIN ONLY ===== */}
        <Route element={<RequireSuperAdmin />}>
          <Route
            path="/superadmindashboard"
            element={<SuperAdminDashboard />}
          />
        </Route>

        {/* 404 Route */}
        <Route
          path="*"
          element={
            <div className="flex items-center justify-center min-h-screen">
              <div className="text-center">
                <h1 className="text-4xl font-bold text-gray-900 mb-4">404</h1>
                <p className="text-gray-600 mb-6">Page not found</p>
                <a
                  href="/"
                  className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                >
                  Go to Login
                </a>
              </div>
            </div>
          }
        />
      </Routes>

      {showOverlay && <SubscriptionOverlay />}
      <SyncIndicator isSyncing={isSyncing} />
    </>
  );
}

export default App;
